import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SITE } from "@/lib/site";

interface NavbarProps {
  // Force the light (scrolled) style — for pages without a dark hero image.
  solid?: boolean;
}

export default function Navbar({ solid = false }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const light = solid || scrolled;

  const navLinks = [
    { label: "Services", to: "/services" },
    { label: "LA Tours", to: "/tours" },
    { label: "FAQ", to: "/faq" },
    { label: "Contact", to: "/contact" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        light
          ? "bg-background-50/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="flex items-center justify-between px-4 md:px-6 lg:px-10 py-3 md:py-4">
        <Link to="/" className="flex items-center gap-2 group" aria-label="GOOD RIDES LA Home">
          <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-md bg-foreground-950 text-background-50">
            <i className="ri-taxi-fill text-sm md:text-base" />
          </div>
          <span
            className={`font-heading text-lg md:text-xl font-semibold tracking-tight transition-colors ${
              light ? "text-foreground-950" : "text-background-50"
            }`}
          >
            GOOD RIDES LA
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-6 lg:gap-8" aria-label="Main navigation">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-sm font-medium tracking-wide transition-colors hover:text-primary-500 ${
                light ? "text-foreground-800" : "text-background-50/90"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${SITE.phone}`}
            className={`hidden lg:inline-flex items-center gap-1.5 text-sm font-medium transition-colors hover:text-primary-500 ${
              light ? "text-foreground-800" : "text-background-50/90"
            }`}
          >
            <i className="ri-phone-fill text-primary-500" />
            <span>{SITE.phoneDisplay}</span>
          </a>
          <Link
            to="/#reservation"
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <span>Book Now</span>
            <i className="ri-arrow-right-line text-sm" />
          </Link>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${
              light ? "text-foreground-950" : "text-background-50"
            }`}
            aria-label="Toggle Menu"
          >
            <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-xl`} />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50/98 backdrop-blur-lg border-t border-background-200/60 px-4 py-5">
          <nav className="flex flex-col gap-4" aria-label="Mobile navigation">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className="text-foreground-800 font-medium py-2 border-b border-background-200/40"
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/#reservation"
              onClick={() => setMobileOpen(false)}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap mt-2"
            >
              <span>Book Now</span>
              <i className="ri-arrow-right-line text-sm" />
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
